// Trail-Geometrie-Helfer für Ride-Mode + ElevationProfile.
//
// Drei Jobs:
//   1. cumulativeDistances(): Meter-vom-Start pro Track-Punkt (Haversine).
//   2. nearestPointOnTrack(): projiziert GPS-Position auf den Track. Liefert
//      "wie weit bin ich schon" (m entlang Track) + "wie weit weg vom Track"
//      (m perpendikular). Wird 1x pro GPS-Update aufgerufen — bei Tracks
//      mit <2000 Punkten unproblematisch (O(N) segmentweise Projektion).
//   3. makeSrtmElevationPoints(): Fallback wenn GPX keine ele hat — sampled
//      Punkte entlang Track und queryt map.queryTerrainElevation.
//
// Alle Funktionen sind pure (keine Side-Effects) — außer der SRTM-Query
// der natürlich auf eine Map-Instance zugreift.

import type { GpxPoint, GpxTrack } from '@adtu/shared';
import { haversine, toRad } from '@adtu/shared';
import type { Map as MLMap } from 'maplibre-gl';

/**
 * Kumulative Distanzen in Metern. Erstes Element ist immer 0, letztes =
 * Gesamt-Track-Länge. Länge des Arrays = points.length.
 */
export function cumulativeDistances(points: GpxPoint[]): number[] {
  const out = new Array<number>(points.length);
  out[0] = 0;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1]!;
    const curr = points[i]!;
    out[i] = (out[i - 1] ?? 0) + haversine(prev.lat, prev.lon, curr.lat, curr.lon);
  }
  return out;
}

export interface NearestOnTrack {
  /** Index des Start-Punkts des Segments auf dem der Fußpunkt liegt (0..N-2) */
  segmentIndex: number;
  /** Parameter 0..1 entlang dieses Segments (0 = Segment-Start, 1 = Segment-Ende) */
  t: number;
  /** Perpendikular-Distanz vom GPS zum Track (Meter) */
  offTrackMeters: number;
  /** Wie weit entlang des Tracks (vom Start gemessen) der Fußpunkt liegt (Meter) */
  alongTrackMeters: number;
  /** Projizierte Koordinate auf dem Track — nützlich fürs Marker-Rendering */
  projectedLat: number;
  projectedLon: number;
}

/**
 * Projiziert (lat, lon) auf den nächstliegenden Punkt auf dem Track.
 *
 * Methode: für jedes Segment perpendikulare Projektion via lokaler
 * Equirectangular-Meter-Projektion (cos(lat)-skaliert). Das ist bei
 * Liguria-Maßstab (≈44°N, einzelne Trails selten >20km) genau auf <1m.
 * Der ausgewählte Fußpunkt ist der mit dem kleinsten euklidischen Abstand.
 *
 * Edge-Cases:
 *  - Track mit <2 Punkten: returned pseudo-nearest am einzigen Punkt mit t=0.
 *  - Mehrere Segmente mit gleichem Abstand (Track kreuzt sich selbst): das
 *    erste gefundene wird genommen. Für MTB-Trails die sich selten kreuzen
 *    reicht das — wer das robuster will, muss "last known alongTrack" als
 *    Hinweis mitgeben (späteres Feature).
 */
/**
 * Wave 14cf-fix5: optionale Snap-Optionen für monotone Progression.
 *
 * Hintergrund: Bei Routen die einen Track-Abschnitt mehrfach überfahren
 * (Out-and-Back, Loops mit gemeinsamem Stück, Kreuzungen) findet die
 * naive globale Suche immer den geometrisch nähesten Punkt — egal ob
 * der zur aktuellen Fahrtrichtung passt. Das Resultat: die orange Next-
 * Section-Aura "springt" zurück auf einen früher überfahrenen Punkt,
 * statt der echten kommenden Strecke zu folgen.
 *
 * Wahoo/Garmin lösen das mit "sticky" map-matching: einmal etabliert,
 * folgt der Snap monoton entlang der GPX, mit kleinem Toleranzfenster
 * für Bewegung in beide Richtungen. Wir machen das gleiche:
 *   - hintAlongMeters: zuletzt bestätigte Track-Position
 *   - lookbackMeters: erlaubter Zurückgang vom Hint (default 200 m —
 *     zur Tolerierung von Rüttler/Stopp-Drift)
 *   - lookaheadMeters: erlaubter Vorgang (default 5000 m — selbst auf
 *     einer schnellen Abfahrt sind 5 km zwischen Snaps unwahrscheinlich)
 *
 * Wenn hintAlongMeters gesetzt ist: bevorzugen wir den nächsten Punkt
 * INNERHALB des Korridors. Nur wenn der Rider weit aus dem Korridor
 * fällt (off-Track > 200 m im Korridor) fallen wir auf die globale Suche
 * zurück — dann ist er ohnehin "lost" und braucht eine Re-Akquise.
 */
export interface NearestPointSnapOptions {
  hintAlongMeters?: number | null;
  lookbackMeters?: number;
  lookaheadMeters?: number;
  /** Wie schlecht der Korridor-Snap sein darf bevor wir auf globale
   *  Suche zurückfallen. Default 150 m — wenn der einzige Korridor-
   *  Kandidat mehr als 150 m off-Track ist, ist der Rider offenbar
   *  vom Plan abgekommen und wir lassen ihn frei re-snappen. */
  globalFallbackOffTrackMeters?: number;
}

export function nearestPointOnTrack(
  points: GpxPoint[],
  lat: number,
  lon: number,
  cumulative?: number[],
  options?: NearestPointSnapOptions,
): NearestOnTrack {
  if (points.length === 0) {
    // defensiv — sollte nie vorkommen, Trails ohne Punkte werden in trails.ts gefiltert.
    return {
      segmentIndex: 0,
      t: 0,
      offTrackMeters: 0,
      alongTrackMeters: 0,
      projectedLat: lat,
      projectedLon: lon,
    };
  }
  if (points.length === 1) {
    const p = points[0]!;
    return {
      segmentIndex: 0,
      t: 0,
      offTrackMeters: haversine(lat, lon, p.lat, p.lon),
      alongTrackMeters: 0,
      projectedLat: p.lat,
      projectedLon: p.lon,
    };
  }

  const cum = cumulative ?? cumulativeDistances(points);

  // Equirectangular-Faktor am User-Breitengrad — für die Segmente drumherum
  // ist cos(userLat) ≈ cos(segLat), der Fehler über 50km-Abstand liegt <0.3%.
  const mPerDegLat = 111320;
  const mPerDegLon = 111320 * Math.cos(toRad(lat));

  // Korridor-Bounds (für monotone Progression). Nur aktiv wenn ein Hint
  // bereitgestellt wurde — sonst klassische globale Suche.
  const hint = options?.hintAlongMeters;
  const useCorridor = hint != null && Number.isFinite(hint);
  const lookback = options?.lookbackMeters ?? 200;
  const lookahead = options?.lookaheadMeters ?? 5000;
  const fallbackThresh = options?.globalFallbackOffTrackMeters ?? 150;
  const corridorFrom = useCorridor ? Math.max(0, hint! - lookback) : 0;
  const corridorTo = useCorridor ? hint! + lookahead : Infinity;

  // Wir tracken zwei Best-Kandidaten:
  //   - bestCorridor: bestes Match INNERHALB [corridorFrom, corridorTo]
  //   - bestGlobal:   absoluter Bester (Fallback wenn Korridor versagt)
  // Im Non-Hint-Fall identisch zur alten Logik (nur globaler).
  let bestCorridorDist = Infinity;
  let bestCorridor: NearestOnTrack | null = null;
  let bestGlobalDist = Infinity;
  let bestGlobal: NearestOnTrack | null = null;

  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]!;
    const b = points[i + 1]!;

    const ax = a.lon * mPerDegLon;
    const ay = a.lat * mPerDegLat;
    const bx = b.lon * mPerDegLon;
    const by = b.lat * mPerDegLat;
    const px = lon * mPerDegLon;
    const py = lat * mPerDegLat;

    const dx = bx - ax;
    const dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    let t = 0;
    if (lenSq > 0) {
      t = ((px - ax) * dx + (py - ay) * dy) / lenSq;
      if (t < 0) t = 0;
      else if (t > 1) t = 1;
    }
    const cx = ax + t * dx;
    const cy = ay + t * dy;
    const d = Math.hypot(px - cx, py - cy);

    const segLen = (cum[i + 1] ?? 0) - (cum[i] ?? 0);
    const alongHere = (cum[i] ?? 0) + t * segLen;

    const candidate: NearestOnTrack = {
      segmentIndex: i,
      t,
      offTrackMeters: d,
      alongTrackMeters: alongHere,
      projectedLat: cy / mPerDegLat,
      projectedLon: cx / mPerDegLon,
    };

    if (d < bestGlobalDist) {
      bestGlobalDist = d;
      bestGlobal = candidate;
    }

    if (
      useCorridor &&
      alongHere >= corridorFrom &&
      alongHere <= corridorTo &&
      d < bestCorridorDist
    ) {
      bestCorridorDist = d;
      bestCorridor = candidate;
    }
  }

  // Korridor-Snap nehmen wenn:
  //   1) Korridor verwendet wurde UND einen Kandidaten hat
  //   2) Der Kandidat ist nicht zu weit weg (sonst Rider ist abgekommen
  //      und wir lassen die globale Suche neu fangen)
  if (
    useCorridor &&
    bestCorridor != null &&
    bestCorridor.offTrackMeters <= fallbackThresh
  ) {
    return bestCorridor;
  }
  return bestGlobal!;
}

/**
 * Schneidet aus einer LineString die Sub-Linie zwischen zwei Distanz-
 * Markern (in Metern entlang der Track-Länge) heraus. Endpunkte werden
 * linear interpoliert (kein Snap auf nächsten Track-Punkt).
 *
 * Wave 14cf: für die Ride-Next-Aura — wir wollen genau die nächsten N
 * Meter ab GPS-Position als orange Aura rendern. `fromMeters` =
 * alongTrackMeters der projizierten GPS-Position, `toMeters` = das + N.
 *
 * Returns: Array von [lon, lat]-Paaren (mind. 0, kann auch nur 1 Punkt
 * sein wenn from==to). Wenn from >= track-Ende: leeres Array.
 *
 * Edge-Cases:
 *  - fromMeters < 0: clampt auf 0
 *  - toMeters > Gesamtlänge: clampt auf Gesamtlänge
 *  - cumulative kann optional übergeben werden (sonst neu berechnet —
 *    sinnvoll wenn der Caller eh schon nearestPointOnTrack mit cum
 *    aufgerufen hat: Wiederverwendung spart die O(N) Schleife).
 */
export function sliceTrackBetween(
  points: GpxPoint[],
  fromMeters: number,
  toMeters: number,
  cumulative?: number[],
): Array<[number, number]> {
  if (points.length < 2) return [];
  const cum = cumulative ?? cumulativeDistances(points);
  const total = cum[cum.length - 1] ?? 0;

  // Clamp + sanity checks
  let from = Math.max(0, Math.min(fromMeters, total));
  let to = Math.max(0, Math.min(toMeters, total));
  if (to <= from) return [];

  // Helper: lineare Interpolation zwischen zwei Track-Punkten an Parameter t
  // (0 = a, 1 = b). Plain linear in lat/lon — über kurze Segmente (<200m)
  // ist der Unterschied zu Greatcircle-Interpolation < 0.1m, für unseren
  // Use-Case irrelevant.
  const lerpPoint = (a: GpxPoint, b: GpxPoint, t: number): [number, number] => [
    a.lon + (b.lon - a.lon) * t,
    a.lat + (b.lat - a.lat) * t,
  ];

  // Finde Segmente die das Intervall [from, to] berühren. Binary-Search
  // wäre für lange Tracks schneller, aber linear ist bei N<5000 trivial.
  const out: Array<[number, number]> = [];

  for (let i = 0; i < points.length - 1; i++) {
    const segStart = cum[i] ?? 0;
    const segEnd = cum[i + 1] ?? segStart;
    const segLen = segEnd - segStart;

    // Segment komplett vor from oder hinter to → skip.
    if (segEnd <= from) continue;
    if (segStart >= to) break;

    const a = points[i]!;
    const b = points[i + 1]!;

    // Erstes berührtes Segment: füge interpolated `from`-Punkt hinzu.
    if (out.length === 0) {
      const tFrom = segLen > 0 ? (from - segStart) / segLen : 0;
      out.push(lerpPoint(a, b, tFrom));
    }

    // Wenn das Segment komplett innerhalb [from, to] liegt: Endpunkt b
    // dranhängen (außer das Segment endet exakt am `to`-Punkt — dann
    // interpolieren wir den letzten Punkt unten).
    if (segEnd < to) {
      out.push([b.lon, b.lat]);
    } else {
      // Segment kreuzt das `to`-Marker: interpolated to-Punkt anhängen.
      const tTo = segLen > 0 ? (to - segStart) / segLen : 1;
      out.push(lerpPoint(a, b, tTo));
      break;
    }
  }

  return out;
}

/**
 * True wenn der Track BRAUCHBARE Höhendaten hat — also
 *   (a) mindestens ein Drittel seiner Punkte haben `ele` und
 *   (b) der Min/Max-Spread der ele-Werte ist ≥ 2m.
 *
 * Wave 14av — Bug-Fix: Vorher hat (a) allein gereicht. Bei Trails mit
 * synthetisch flachen GPX-Daten (z.B. alle Punkte mit `ele=523`, wie sie
 * manche GPX-Konverter oder manuell erstellte Tracks produzieren) hat
 * useTrailElevation dann den GPX-Pfad genommen und einen 523m-Flatline
 * gerendert — OHNE jemals SRTM oder HTTP-Cascade zu konsultieren. Der
 * "Rebuild"-Button hat zwar den IDB-Cache gelöscht, aber da der Cache in
 * diesem Code-Pfad überhaupt nicht konsultiert wird, hatte er keinen
 * Effekt. Symptom: Button zeigt dauerhaft "No data" weil der Hook-Effect
 * bei jedem Rerun sofort wieder in source='gpx' landet.
 *
 * Neue Regel (b): Wenn alle vorhandenen ele-Werte innerhalb 2m liegen,
 * behandeln wir sie als fake/unbrauchbar — genauso als wären sie gar
 * nicht da. Das triggert dann SRTM/HTTP-Pipeline, die echte Samples holt
 * und in IDB cached. Für echte flache Trails (Ligurischer Küstenradweg
 * z.B.) ist 2m nicht einschränkend — GPS-Noise allein produziert mehr.
 */
export function hasElevationData(track: GpxTrack): boolean {
  if (track.points.length === 0) return false;
  let withEle = 0;
  let min = Infinity;
  let max = -Infinity;
  for (const p of track.points) {
    if (p.ele != null && Number.isFinite(p.ele)) {
      withEle++;
      if (p.ele < min) min = p.ele;
      if (p.ele > max) max = p.ele;
    }
  }
  if (withEle / track.points.length < 0.33) return false;
  if (max - min < 2) return false;
  return true;
}

/**
 * Sampled n Punkte entlang des Tracks gleichmäßig nach Distanz, holt für
 * jeden Punkt map.queryTerrainElevation(). Returns null wenn Terrain nicht
 * verfügbar (Style ohne Hillshade → dann zeigt das UI halt "keine Höhe").
 *
 * Warum sampling statt pro Track-Punkt? Bei 1000+ Track-Punkten wäre das
 * 1000+ Tile-Lookups — macht bei nicht-geladenen DEM-Tiles schnell Probleme.
 * Mit 100 Samples ist das Profil trotzdem hinreichend glatt für MTB-UI-Zwecke.
 *
 * Sample-Strategie: gleichmäßig verteilt entlang der Track-Länge. Für jeden
 * Sample wird der Punkt zwischen den zwei umgebenden Track-Punkten linear
 * interpoliert (Lat/Lon), dann queryTerrainElevation darauf.
 */
export async function buildSrtmElevationSamples(
  track: GpxTrack,
  map: MLMap,
  sampleCount = 100,
): Promise<number[] | null> {
  const points = track.points;
  if (points.length < 2) return null;

  const cum = cumulativeDistances(points);
  const total = cum[cum.length - 1] ?? 0;
  if (total === 0) return null;

  const out = new Array<number>(sampleCount);
  // Nicht-geladene DEM-Tiles: queryTerrainElevation kann `null`/`undefined`
  // zurückgeben. Wir ersetzen durch letztes valides Sample (smoothes Profil)
  // und tracken ob wir überhaupt was bekommen haben.
  let lastValid: number | null = null;
  let validCount = 0;
  let minElev = Infinity;
  let maxElev = -Infinity;

  for (let s = 0; s < sampleCount; s++) {
    const targetDist = (s / (sampleCount - 1)) * total;
    // Binäre/lineare Suche — bei ~2000 Punkten macht linear keinen Unterschied.
    let segI = 0;
    while (segI < cum.length - 1 && (cum[segI + 1] ?? 0) < targetDist) segI++;
    const dStart = cum[segI] ?? 0;
    const dEnd = cum[segI + 1] ?? dStart;
    const segLen = dEnd - dStart;
    const t = segLen > 0 ? (targetDist - dStart) / segLen : 0;

    const a = points[segI]!;
    const b = points[Math.min(segI + 1, points.length - 1)]!;
    const lat = a.lat + (b.lat - a.lat) * t;
    const lon = a.lon + (b.lon - a.lon) * t;

    let elev: number | null = null;
    try {
      const v = map.queryTerrainElevation([lon, lat]);
      if (v != null && Number.isFinite(v)) elev = v;
    } catch {
      // ignorieren — es kommt lastValid oder 0 als Fallback
    }

    if (elev != null) {
      validCount++;
      lastValid = elev;
      out[s] = elev;
      if (elev < minElev) minElev = elev;
      if (elev > maxElev) maxElev = elev;
    } else {
      out[s] = lastValid ?? 0;
    }
  }

  // Wave 14aq: höhere Validity-Hürde.
  //
  // Vorher: "≥1 Sample valide" → Wenn nur EIN DEM-Tile zufällig geladen war
  // (z.B. unter dem Anfang des Tracks), kopierte der `lastValid`-Fallback
  // diesen einen Wert in alle 100 Slots → flacher Strich, Gain/Loss=0, UI
  // sieht aus wie "SRTM funktioniert nicht". Beobachtetes Symptom bei
  // "Appendaun": Chart zeigt 523m flat, Stats ↓0/↑0.
  //
  // Neue Hürden:
  //   - ≥30% valide Samples (schützt gegen "nur vorderes Tile geladen")
  //   - Min/Max-Spread ≥ 2m (schützt gegen "alle Samples innerhalb eines
  //     einzigen Tiles, also echt flach ODER dieselben Daten dupliziert")
  //
  // Bei Verletzung: null zurückgeben → useTrailElevation retry-Loop nimmt
  // einen weiteren Anlauf in 1.5s / 4s, in der Hoffnung dass dann mehr
  // DEM-Tiles geladen sind. Ein 2m-Spread über 100 Samples ist für einen
  // MTB-Trail pragmatisch immer gegeben (2m Höhenunterschied auf ~2km).
  // Wave 14as: Diagnostik-Logs. Im Safari-Devtools kann der Admin auf dem
  // iPhone sehen WARUM ein Rebuild scheitert (z.B. "only 12/100 valid" →
  // DEM-Tiles für den Grossteil der Trail-Bbox nicht geladen, trotz fitBounds).
  if (validCount < Math.floor(sampleCount * 0.3)) {
    console.info(
      `[elev] sampler reject: only ${validCount}/${sampleCount} valid (<30%) — ` +
      `DEM-Tiles nicht vollständig geladen für die Trail-Bbox.`,
    );
    return null;
  }
  if (maxElev - minElev < 2) {
    console.info(
      `[elev] sampler reject: spread ${(maxElev - minElev).toFixed(2)}m < 2m ` +
      `(min=${minElev.toFixed(0)} max=${maxElev.toFixed(0)}) — flatline case, ` +
      `vermutlich nur 1 DEM-Tile geladen und lastValid-Fallback hat alles gefüllt.`,
    );
    return null;
  }
  console.info(
    `[elev] sampler accept: ${validCount}/${sampleCount} valid, ` +
    `spread ${(maxElev - minElev).toFixed(0)}m (${minElev.toFixed(0)}..${maxElev.toFixed(0)}m).`,
  );
  return out;
}

/**
 * Wave 14bk: Moving-Average-Smoothing fürs Höhenprofil.
 *
 * User-Intent (Dondella-Screenshot): der Trail hat in der Aufzeichnungs-App
 * des Users ein glattes Profil, aber bei uns sieht dasselbe GPX/SRTM mit
 * rohen Samples gezackt aus. Der Grund ist Punkt-zu-Punkt-Noise (GPS-ele
 * oder SRTM-Rasterung), der beim Linien-Rendering als Zickzack sichtbar
 * wird. Moving-Average glättet das ohne Information zu verlieren — echte
 * Peaks/Sättel bleiben, nur die hochfrequenten Zuckungen zwischen
 * Nachbar-Samples werden weggemittelt.
 *
 * Parameter:
 *   - samples: rohes Sample-Array (N Punkte)
 *   - window: Fenstergröße (symmetrisch um Sample i). Default 9 — bei
 *     SAMPLE_COUNT=100 smoothen wir also über ~9% der Track-Länge. Bei
 *     einem 3km-Trail sind das ~270m pro Average-Fenster, das entfernt
 *     GPS-Noise komplett aber lässt kürzere echte Kicker (≥300m lang)
 *     als sichtbaren Hub im Profil stehen.
 *
 * Edge-Handling: an den Enden schrumpft das Fenster SYMMETRISCH bis es
 * nur noch den einen Endsample enthält. Damit gilt smoothed[0] === raw[0]
 * und smoothed[N-1] === raw[N-1]. Wichtig weil User "Startpunkt liegt bei
 * 1040 m" oder "Gipfel ist 8848 m" kommunizieren möchten — eine asymmetrisch
 * geclippte Mittelung würde den Endpunkt mit den N inneren Nachbarn mischen
 * und sichtbar dämpfen (z. B. Everest-Gipfel: raw 8848 m → asymmetrisch
 * geglättet 8231 m, böser UX-Bruch zu dem was der User in seiner Aufzeichnungs-
 * App sieht).
 *
 * Tuning: Probiert wurden Windows 5/7/9/11. 5 lässt noch sichtbare Zacken,
 * 11 fängt an echte kurze Kicker zu dämpfen. 9 ist der Sweet-Spot der im
 * Dondella-Vergleich die Optik der User-App am besten nachbildet.
 */
/**
 * Wave 14cc: Despike-Filter — entfernt einzelne Ausreisser aus Sample-Arrays.
 *
 * Hintergrund: `queryTerrainElevation` kann bei Tile-Boundaries, nicht-
 * geladenen Nachbartiles oder schlicht korrupten DEM-Pixeln gelegentlich
 * krasse Falschwerte zurückgeben — beobachtet im Liguria-Test: ein einzelner
 * Sample mit 1273 m mitten in einer Sequenz von ~900–1050 m. Smoothing
 * allein bügelt den nicht raus (mittelt nur über 9 Samples mit Window),
 * dadurch entstand eine "fake Kuppe", die `↑ 1097 m` Total-Ascent
 * pathologisch aufgebläht hat.
 *
 * Algorithmus (median-of-3): für jeden Sample i prüfen wir, ob er deutlich
 * von BEIDEN Nachbarn abweicht (>= maxJumpMeters). Wenn ja, ist er ein
 * Ausreisser und wird durch den Median der 3-Sample-Nachbarschaft ersetzt.
 * Endpunkte bleiben unangetastet (keine Nachbarn rechts/links zum Vergleich).
 *
 * Default maxJumpMeters=80: bei typischer Sampling-Dichte (50–100 m
 * horizontal pro Sample) entspricht 80 m Höhenunterschied ~80–160 % Steigung,
 * was ein Klippenabsatz wäre — auf MTB-Trails physikalisch nicht. Echte
 * harte Steigungen (z.B. 30 % Grade = 30 m Hub pro 100 m Sample) bleiben
 * unter der Schwelle und werden NICHT als Spike geflaggt.
 *
 * Aufrufreihenfolge: despike VOR smooth. Smooth-Window 9 würde sonst den
 * Spike auf 9 Samples verteilen statt zu eliminieren.
 */
export function despikeElevationSamples(
  samples: number[],
  windowHalfSize = 5,
  madMultiplier = 4,
): number[] {
  const n = samples.length;
  if (n < 3) return samples.slice();
  const out = samples.slice();

  // Hampel-Filter: für jeden Sample i bilden wir ein Fenster
  // [i-k..i+k] (mit k = windowHalfSize), berechnen Median + Median-Absolute-
  // Deviation (MAD). Wenn der Sample mehr als `madMultiplier * MAD` vom
  // Median abweicht, ist er ein Outlier und wird durch den Median ersetzt.
  //
  // Wave 14cc-fix2: Vorher war es ein 3-Sample-Filter, der NUR einzelne
  // Spikes erkannte. Hampel mit k=5 (Fenster 11) toleriert bis zu 5
  // schlechte Samples in Folge — wenn das DEM für eine ganze Region
  // glitchy ist (z.B. Liguria-Test: "Buckel" am Trail-Anfang aus 5-10
  // hintereinander wrong samples), schluckt der Hampel das.
  //
  // MAD ist robust gegen Outlier im Window selbst: bei bis zu 50% bad
  // samples bleibt der Median korrekt. 4 × MAD entspricht ungefähr 2.7σ
  // bei normaler Verteilung — passt nicht-glitchy MTB-Profilen, killt
  // aber alles was deutlich rausfällt.
  //
  // Floor MAD bei 10 m: bei flachen Profilen wäre MAD≈0 und JEDE kleine
  // Variation würde als Outlier geflaggt. Floor stellt sicher dass nur
  // echte 40 m+ Abweichungen rausfliegen.
  const MAD_FLOOR = 10;
  let spikeCount = 0;
  for (let i = 0; i < n; i++) {
    const lo = Math.max(0, i - windowHalfSize);
    const hi = Math.min(n - 1, i + windowHalfSize);
    const window: number[] = [];
    for (let j = lo; j <= hi; j++) {
      const v = samples[j];
      if (v != null && Number.isFinite(v)) window.push(v);
    }
    if (window.length < 3) continue;
    const med = median(window);
    const deviations: number[] = window.map((v) => Math.abs(v - med));
    const mad = Math.max(median(deviations), MAD_FLOOR);
    const cur = samples[i]!;
    if (!Number.isFinite(cur)) continue;
    if (Math.abs(cur - med) > madMultiplier * mad) {
      out[i] = med;
      spikeCount++;
    }
  }
  if (spikeCount > 0) {
    console.info(
      `[elev] despike (Hampel): ${spikeCount}/${n} Samples geglättet ` +
        `(Fenster ±${windowHalfSize}, ${madMultiplier}×MAD)`,
    );
  }
  return out;
}

/** Einfacher Median für Zahlenarrays. Sortiert eine Kopie — bei N≤11
 *  (unsere Hampel-Fenstergrösse) ist O(N log N) trivial. */
function median(arr: number[]): number {
  if (arr.length === 0) return 0;
  const sorted = arr.slice().sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  return sorted.length % 2 === 0
    ? (sorted[mid - 1]! + sorted[mid]!) / 2
    : sorted[mid]!;
}

/**
 * Wave 14cc-fix3: Grade-basiertes Despiking für sustained Bad-Zones.
 *
 * Motivation: in der Custom-Tour "Isallo Extasy" haben wir empirisch
 * (siehe scripts/diag-elevation-zones.mjs / Logs vom User-Test) eine
 * Sequenz von 7 aufeinanderfolgenden Samples mit Grades von 102%–277%
 * gefunden — physikalisch unmöglich (Klippen sind 100%, 270% wäre
 * senkrechter Lift). Ursache: die DEM-Source-Tiles für den Aufstieg
 * Richtung Melogno haben eine korrupte/offsetted Region. Beim
 * Route-Build wurde diese falsche Höhen vom Server abgegriffen und
 * in die GPX <ele>-Tags geschrieben.
 *
 * Hampel-Filter (Wave 14cc-fix2) kam damit nicht klar: 7+ Bad-Samples
 * in Folge ziehen den Median des Fensters mit ins kaputte Plateau,
 * sodass kein Sample mehr als Outlier auffällt.
 *
 * Algorithmus:
 *   1. Berechne Grade zwischen jedem Sample-Paar.
 *   2. Markiere Samples deren EINGANGS-Grade > maxGrade als bad-edge.
 *   3. Cluster benachbarte bad-edges zu Bad-Zones (Hysterese: lasse
 *      bis zu `gapTolerance` gute Samples dazwischen drin — verhindert
 *      Auftrennen einer Zone wenn ein einzelner Sample zufällig
 *      grenzwertig ist).
 *   4. Für jede Bad-Zone [i..j]: ersetze elev[i..j] durch lineare
 *      Interpolation zwischen elev[i-1] und elev[j+1]. Boundary-Edge-
 *      Fälle (Zone berührt Array-Start/Ende): nimm das andere Ende
 *      bzw. lasse unangetastet.
 *
 * Die ersetzten Werte spiegeln NICHT die echte Topographie wider, sind
 * aber strukturell besser als eine 460-m-Fakebump: Total-Gain wird
 * realistisch, Profile bleibt visuell glaubwürdig.
 *
 * @param samples — gleichmässig entlang der Strecke verteilte Höhen
 * @param horizontalStepM — horizontale Distanz zwischen zwei
 *   benachbarten Samples (= totalMeters / (sampleCount-1)). Konstant
 *   da unsere Sampler immer evenly-spaced sind.
 * @param maxGrade — physikalisches Grade-Limit, default 0.5 (50% = 27°).
 *   Echte Trail-Steigungen erreichen kurzfristig 30%, dauerhaft <15%.
 * @param gapTolerance — wieviele "gute" Samples dürfen innerhalb
 *   einer Bad-Zone toleriert werden (default 1).
 */
export function clampElevationByGrade(
  samples: number[],
  horizontalStepM: number,
  maxGrade = 0.5,
  gapTolerance = 1,
): number[] {
  const n = samples.length;
  if (n < 3 || horizontalStepM <= 0) return samples.slice();
  const maxJump = horizontalStepM * maxGrade;

  // Schritt 1+2: für jedes Sample i ≥ 1 prüfen wir |samples[i]−samples[i-1]|.
  // Wenn > maxJump → samples[i] ist bad-edge-leading.
  const isBadEdge = new Array<boolean>(n).fill(false);
  for (let i = 1; i < n; i++) {
    const delta = Math.abs(samples[i]! - samples[i - 1]!);
    if (delta > maxJump) isBadEdge[i] = true;
  }

  // Schritt 3: Bad-Zones bilden. Cluster aller bad-edges plus
  // Toleranz-Gaps. Ein bad-edge bei i markiert mindestens [i-1, i] als
  // Bad-Zone (weil EINER von beiden der falsche Wert sein muss).
  const out = samples.slice();
  let totalReplaced = 0;
  let i = 0;
  while (i < n) {
    if (!isBadEdge[i]) {
      i++;
      continue;
    }
    // Zone-Start: das vorletzte Sample dass den fehlerhaften Sprung
    // produziert hat, also i-1 wenn vorhanden.
    let zoneStart = Math.max(0, i - 1);
    // Zone-Ende: erweitere solange weitere bad-edges in Reichweite
    // (gapTolerance gute Samples dazwischen) auftauchen.
    let zoneEnd = i;
    let gap = 0;
    let probe = i + 1;
    while (probe < n) {
      if (isBadEdge[probe]) {
        zoneEnd = probe;
        gap = 0;
      } else {
        gap++;
        if (gap > gapTolerance) break;
      }
      probe++;
    }

    // Schritt 4: linear interpolieren zwischen den BOUNDARY-Samples.
    // Boundary = Samples direkt vor + direkt nach der Zone. Wenn Zone
    // an den Rand stösst, nimm den Sample am Rand selbst.
    const anchorLeftIdx = Math.max(0, zoneStart - 1);
    const anchorRightIdx = Math.min(n - 1, zoneEnd + 1);
    const anchorLeft = out[anchorLeftIdx]!;
    const anchorRight = out[anchorRightIdx]!;
    const span = anchorRightIdx - anchorLeftIdx;
    if (span > 0) {
      for (let k = zoneStart; k <= zoneEnd; k++) {
        const t = (k - anchorLeftIdx) / span;
        out[k] = anchorLeft + (anchorRight - anchorLeft) * t;
        totalReplaced++;
      }
    }
    i = zoneEnd + 1;
  }

  if (totalReplaced > 0) {
    console.info(
      `[elev] grade-clamp: ${totalReplaced}/${n} Samples durch lineare ` +
        `Interpolation ersetzt (Grenze ${(maxGrade * 100).toFixed(0)}% Grade, ` +
        `Step ${horizontalStepM.toFixed(0)} m → max Hub ${maxJump.toFixed(0)} m)`,
    );
  }
  return out;
}

export function smoothElevationSamples(samples: number[], window = 9): number[] {
  const n = samples.length;
  if (n === 0) return [];
  if (window < 2) return samples.slice();
  const half = Math.floor(window / 2);
  const out = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    // Symmetrisches Shrinking: das Fenster darf an beiden Seiten höchstens
    // so weit reichen wie der nähere Rand zulässt. min(half, i, n-1-i) hält
    // beide Hälften gleich gross — am Endpunkt (i=0 oder i=n-1) ist das 0
    // und das Fenster kollabiert auf den Sample selbst.
    const symHalf = Math.min(half, i, n - 1 - i);
    let sum = 0;
    let cnt = 0;
    for (let j = i - symHalf; j <= i + symHalf; j++) {
      const v = samples[j]!;
      if (Number.isFinite(v)) {
        sum += v;
        cnt++;
      }
    }
    out[i] = cnt > 0 ? sum / cnt : samples[i]!;
  }
  return out;
}

/**
 * Macht aus einem Sample-Array (z.B. 100 Höhen) ein parallel-indiziertes
 * Distanz-Array. Zusammen sind das dann die X/Y-Werte fürs Profil-SVG.
 */
export function sampleDistances(totalMeters: number, count: number): number[] {
  const out = new Array<number>(count);
  for (let i = 0; i < count; i++) {
    out[i] = (i / (count - 1)) * totalMeters;
  }
  return out;
}
